import { useCallback } from 'react'
import type { ConnectedWallet } from '@towns/privy'
import type { UserOps } from '@towns/userops'
import { providers } from 'ethers'
import type { GetSigner } from 'privy/WalletReady'
import type { Address } from 'viem'

export class SignatureApprovalError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'SignatureApprovalError'
    }
}

export type SignatureApprovalType = 'personal' | 'typed'

export type SignatureApprovalRequest = {
    type: SignatureApprovalType
    payload: string
    chainId: number
    signerWallet?: Address
    allowEIP1271: boolean
}

type SignatureExecutorParams = {
    selectedWallet: Address
    abstractAccountAddress?: Address
    request: SignatureApprovalRequest
    getRootKeySigner: GetSigner
}

type SignatureApprovalExecutorArgs = {
    connectedWallets: ConnectedWallet[]
    userOpsInstance: UserOps | undefined
}

export const useSignatureApprovalExecutor = ({
    connectedWallets,
    userOpsInstance,
}: SignatureApprovalExecutorArgs) => {
    const executeSignature = useCallback(
        async ({
            selectedWallet,
            abstractAccountAddress,
            request,
            getRootKeySigner,
        }: SignatureExecutorParams): Promise<string> => {
            if (request.type !== 'personal' && request.type !== 'typed') {
                throw new Error('Unsupported signature type')
            }

            const normalizedSelectedWallet = selectedWallet.toLowerCase()
            const requiredWallet = request.signerWallet?.toLowerCase()

            if (requiredWallet && normalizedSelectedWallet !== requiredWallet) {
                throw new SignatureApprovalError(
                    'Selected wallet does not match the required signer',
                )
            }

            const isAbstractAccount =
                normalizedSelectedWallet === abstractAccountAddress?.toLowerCase()

            if (isAbstractAccount && !request.allowEIP1271) {
                throw new SignatureApprovalError(
                    'Smart account signatures are not allowed for this request',
                )
            }

            if (request.type === 'personal') {
                if (!isAbstractAccount) {
                    throw new Error('Personal sign not supported for EOA wallets')
                }
                return signWithSmartAccount({
                    request,
                    getRootKeySigner,
                    userOpsInstance,
                })
            }

            if (isAbstractAccount) {
                return signWithSmartAccount({
                    request,
                    getRootKeySigner,
                    userOpsInstance,
                })
            }

            const connectedWallet = connectedWallets.find(
                (wallet) => wallet.address.toLowerCase() === normalizedSelectedWallet,
            )

            if (!connectedWallet) {
                throw new SignatureApprovalError('Wallet not connected')
            }

            return signTypedDataWithEOA({
                request,
                connectedWallet,
            })
        },
        [connectedWallets, userOpsInstance],
    )

    return {
        executeSignature,
    }
}

const signWithSmartAccount = async ({
    request,
    getRootKeySigner,
    userOpsInstance,
}: Pick<SignatureExecutorParams, 'request' | 'getRootKeySigner'> & {
    userOpsInstance: UserOps | undefined
}) => {
    if (!userOpsInstance) {
        throw new SignatureApprovalError('Smart account client not available')
    }

    const signer = await getRootKeySigner()
    if (!signer) {
        throw new SignatureApprovalError('Failed to get root key signer')
    }

    const smartAccount = await userOpsInstance.smartAccountSession.getSmartAccount({
        signer,
    })

    const smartAccountClient = smartAccount?.client
    if (!smartAccountClient) {
        throw new SignatureApprovalError('Smart account client not available')
    }

    if (request.type === 'personal') {
        if (!smartAccountClient.account) {
            throw new SignatureApprovalError('Smart account account not available')
        }
        return smartAccountClient.signMessage({
            message: request.payload,
            account: smartAccountClient.account,
        })
    }

    const typedData = JSON.parse(request.payload)
    return smartAccountClient.signTypedData(typedData)
}

const signTypedDataWithEOA = async ({
    request,
    connectedWallet,
}: {
    request: SignatureApprovalRequest
    connectedWallet: ConnectedWallet
}) => {
    let privyProvider = await connectedWallet.getEthereumProvider()
    let provider = new providers.Web3Provider(privyProvider as providers.ExternalProvider)

    const currentNetwork = await provider.getNetwork()
    if (currentNetwork.chainId !== request.chainId) {
        try {
            await privyProvider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: `0x${request.chainId.toString(16)}` }],
            })
        } catch {
            throw new SignatureApprovalError('Chain switch failed')
        }

        privyProvider = await connectedWallet.getEthereumProvider()
        provider = new providers.Web3Provider(privyProvider as providers.ExternalProvider)
    }

    const signer = provider.getSigner()
    const typedData = JSON.parse(request.payload)

    if ('_signTypedData' in signer && typeof signer._signTypedData === 'function') {
        return signer._signTypedData(typedData.domain, typedData.types, typedData.message)
    }

    throw new SignatureApprovalError('Signer does not support typed data signing')
}
